var _dec, _dec2, _class, _descriptor, _descriptor2, _descriptor3;
function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'transform-class-properties is enabled and runs after the decorators transform.'); }
import Service, { inject as service } from '@ember/service';
import { computed } from '@ember/object';
import { filterBy } from '@ember/object/computed';
import environment from '@square/environment-utils/environment';
import ThirdPartyMenuMigrationService from '@dashboard/shared-ui/clients/third-party-menu-migration-service';
import { MenuMigrationStartedRequest, MenuMigrationStartedResponse, PrepareThirdPartyMenuMigrationRequest, UpdateMenuMigrationRequest, UpdateMenuMigrationResponse } from '@square/dashboard-protos/models/squareup/rstonboard/service';
import { WoflowLink } from '@woflowinc/link';
export let WoflowOnDataStatesEnum = /*#__PURE__*/function (WoflowOnDataStatesEnum) {
  WoflowOnDataStatesEnum["IN_PROGRESS"] = "in_progress";
  WoflowOnDataStatesEnum["PENDING_APPROVAL"] = "pending_approval";
  WoflowOnDataStatesEnum["EXPORT_IN_PROGRESS"] = "export_in_progress";
  return WoflowOnDataStatesEnum;
}({});
export let WoflowLinkConfiguration = /*#__PURE__*/function (WoflowLinkConfiguration) {
  WoflowLinkConfiguration["MIGRATE_MENU"] = "MIGRATE_MENU";
  WoflowLinkConfiguration["UPLOAD_MENU"] = "UPLOAD_MENU";
  return WoflowLinkConfiguration;
}({});
export const WOFLOW_CONFIG = {
  STAGING: {
    PUBLIC_KEY: 'e083c24204d8862080f347b57525a836',
    LINK_CONFIGS: {
      MIGRATE_MENU: '237a4e86-6c0e-4d2f-836f-3f836f1dec83',
      UPLOAD_MENU: '7050c9d9-152e-4875-8f35-7bc8a17a6bca'
    }
  },
  PRODUCTION: {
    PUBLIC_KEY: 'b308d7f0b2dc829cd797e922e82da0fe',
    LINK_CONFIGS: {
      MIGRATE_MENU: '24a3677d-7baf-458c-8804-fc6986d91a44',
      UPLOAD_MENU: '7887ddfb-1e02-42dc-97ee-f2194ac72ee1'
    }
  }
};

/**
 * Service for integrating Woflow functionality within an Ember application.
 * This service is responsible for:
 * - Generating a JWT for Woflow authentication using the selected merchant unit's details and a
 * menu import token
 * - Initializing Woflow with specific configurations
 * - Handling the woflow link iframe
 * - Updating menu attributes
 *
 * @example
 * ```javascript
 * import Component from '@glimmer/component';
 * import { inject as service } from '@ember/service';
 *
 * export default class ExampleComponent extends Component {
 *   @service('restaurants/woflow-link-service') woflowLinkService;
 *
 *   someMethod() {
 *     this.woflowLinkService.initialize(WoflowLinkConfiguration.UPLOAD_MENU, onData, onClose, onError);
 *   }
 * }
 * ```
 *
 * @example
 * ```javascript
 * // For non-omnichannel menus, only the menuName is required
 * await this.woflowLink.updateMenuAttributes({ menuName });
 * ```
 *
 * // For omnichannel menus, there's the `
 *  omnichannelAttributes: {
 *     unitTokens,
 *     channelIds,
 *     availabilityPeriods,
 *     onlineChannelsVisible
 *   }` that need to be
 *   provided
 * ```javascript
 *   await this.woflowLink.updateMenuAttributes({
 *     menuName: args.menuName,
 *     omnichannelAttributes: {
 *       unitTokens: args.unitTokens,
 *       channelIds: args.channels.toArray(),
 *       availabilityPeriods: args.availabilityPeriods?.map((a) => a.id) ?? [],
 *       onlineChannelsVisible: true | false,
 *     },
 *   });
 * ```
 *
 * @class WoflowLinkService
 * @extends Service
 */
let WoflowLinkService = (_dec = computed('userInfo.merchantToken'), _dec2 = filterBy('locationSelector.sortedUnits', 'unitActive'), (_class = class WoflowLinkService extends Service {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "userInfo", _descriptor, this);
    _initializerDefineProperty(this, "locationSelector", _descriptor2, this);
    _defineProperty(this, "menuImportToken", null);
    _initializerDefineProperty(this, "sortedUnits", _descriptor3, this);
  }
  get merchantToken() {
    return this.userInfo.merchantToken;
  }
  get initialSelectedLocation() {
    return this.sortedUnits[0];
  }

  /**
   * Initializes the [Woflow link](https://docs.woflow.com/link/sdk) with the given link
   * configuration and optional callbacks for data and close events.
   *
   * 1. Prepares the Woflow link by generating menu_import
   *    if one doesn't exist, or retrieves an existing on in status NOT_STARTED,
   *    embeds it in a JWT for Woflow authentication, and stores the menu import token for updates.
   * 2. Initializes Woflow with the generated JWT and the given link configuration
   * 3. Handles data and close events from the Woflow iframe through callbacks
   *
   * @param {WoflowLinkConfiguration} linkConfigurationType - The type of Woflow link configuration
   *   to initialize.
   * @param {(data: any) => void} [onData] - Optional callback to handle data received from Woflow.
   * @param {() => void} [onClose] - Optional callback to handle the closing of the Woflow iframe.
   * @param onError - Optional callback to handle errors that occur during the Woflow onData callback.
   * @returns {Promise<void>}
   */
  async initialize(linkConfigurationType, onData, onClose, onError) {
    const env = environment();
    const {
      PUBLIC_KEY,
      LINK_CONFIGS
    } = env.isProduction ? WOFLOW_CONFIG.PRODUCTION : WOFLOW_CONFIG.STAGING;
    const linkConfigurationId = LINK_CONFIGS[linkConfigurationType];
    let jwt;
    try {
      jwt = await this.prepareWoflowLink();
    } catch {
      throw new Error('Failed to prepare Woflow link');
    }
    const options = {
      jwt,
      publicKey: PUBLIC_KEY,
      linkConfigurationId
    };
    const link = this.createWoflowLink(options);
    link.openIframe({
      onData: async data => {
        const isInProgress = data?.state === WoflowOnDataStatesEnum.IN_PROGRESS || data?.state === WoflowOnDataStatesEnum.EXPORT_IN_PROGRESS;
        const menuMigrationType = this.mapWoflowLinkConfigToMigrationType(linkConfigurationType);
        if (isInProgress) {
          try {
            await this.updateMenuImportStatusToInProgress(menuMigrationType);
            if (onData) {
              await onData(data);
            }
          } catch (error) {
            if (onError) {
              // Notify caller of woflow-link-service that an error occurred
              onError(error);
            }
          }
        }
      },
      onClose: () => {
        if (onClose) {
          // No need to update status here since status will remain in NOT_STARTED,
          // and it will be cleared with ExpiredWoflowMenuImportCronJob
          onClose();
        }
      }
    });
  }

  /**
   * Updates the menu attributes based on the provided parameters.
   * If the `omnichannelAttributes` property is present in the parameters, the menu is considered
   * to be omnichannel, and the method will use the `unitTokens` and `channelIds` for the update.
   * If the `omnichannelAttributes` property is not present, it's a non-omnichannel menu, and the
   * method will use the `unitToken` of the initially selected location. In both cases, the stored
   * `menuImportToken` is used in the request.
   *
   * @param {UpdateMenuAttributesParams} params - The parameters for the menu update.
   * @throws Will throw an error if `menuImportToken` is not available.
   * @returns {Promise<UpdateMenuMigrationResponse>}
   *
   * @example
   * // Update a non-omnichannel menu
   * this.woflowLinkService.updateMenuAttributes({ menuName: 'Test Menu' });
   *
   * @example
   * // Update an omnichannel menu
   * this.woflowLinkService.updateMenuAttributes({
   *   menuName: 'Test Menu',
   *   omnichannelAttributes: {
   *     unitTokens: ['unit1', 'unit2'],
   *     channelIds: ['channel1', 'channel2'],
   *     availabilityPeriodIds: ['period1', 'period2'],
   *     onlineChannelsVisible: true,
   *   }
   * });
   */
  async updateMenuAttributes(params) {
    const {
      menuName
    } = params;
    if (!this.menuImportToken) {
      throw new Error('No menuImportToken is available');
    }
    let request;
    if ('omnichannelAttributes' in params) {
      const {
        omnichannelAttributes: {
          unitTokens,
          channelIds,
          availabilityPeriodIds,
          onlineChannelsVisible
        }
      } = params;
      request = UpdateMenuMigrationRequest.create({
        menuImportToken: this.menuImportToken,
        menuName,
        isOmnichannel: true,
        unitTokens,
        channelIds,
        availabilityPeriodIds,
        onlineChannelsVisible
      });
    } else {
      const {
        token: unitToken
      } = this.initialSelectedLocation;
      request = UpdateMenuMigrationRequest.create({
        menuImportToken: this.menuImportToken,
        menuName,
        unitTokens: [unitToken]
      });
    }
    const response = await ThirdPartyMenuMigrationService.updateMenuMigration(request);
    if (response.status === UpdateMenuMigrationResponse.Status.SUCCESS) {
      return response;
    } else if (response.error === UpdateMenuMigrationResponse.Error.NOT_ACCEPTING_MENU_MIGRATIONS) {
      // Feature flag restaurants/use_external_menu_migration_service is disabled
      throw new Error('Not accepting menu migrations at this time.');
    } else {
      throw new Error('Unknown error occurred while updating menu.');
    }
  }

  /**
   * Generates a JWT for Woflow authentication using the selected merchant unit's details and a
   * menu import token. This method is private and not intended for direct external use.
   *
   * @returns {Promise<string>} The JWT token for Woflow authentication.
   * @private
   */
  async prepareWoflowLink() {
    const {
      token: unitToken,
      nickname: unitNickname
    } = this.initialSelectedLocation;
    const payload = {
      unitToken,
      unitNickname
    };
    const request = PrepareThirdPartyMenuMigrationRequest.create(payload);
    const response = await ThirdPartyMenuMigrationService.prepareThirdPartyMenuMigration(request);
    const {
      menuImportToken,
      thirdPartyToken
    } = response;

    // Store the menu import token for use when updating menu status, menu name, and omnichannel attrs
    this.menuImportToken = menuImportToken;
    return thirdPartyToken;
  }

  /**
   * Instantiates WoflowLink, primarily to help mocking in tests.
   *
   * @param {WoflowLinkOptions} options Configuration options for WoflowLink.
   * @returns {WoflowLink} An instance of WoflowLink.
   */
  createWoflowLink(options) {
    return new WoflowLink(options);
  }
  async updateMenuImportStatusToInProgress(menuMigrationType) {
    if (!this.menuImportToken) {
      throw new Error('No menuImportToken is available');
    }
    const request = MenuMigrationStartedRequest.create({
      menuImportToken: this.menuImportToken,
      type: menuMigrationType
    });
    const response = await ThirdPartyMenuMigrationService.menuMigrationStarted(request);
    if (response.status !== MenuMigrationStartedResponse.Status.SUCCESS) {
      if (response.error === MenuMigrationStartedResponse.Error.NOT_ACCEPTING_MENU_MIGRATIONS) {
        // Feature flag restaurants/use_external_menu_migration_service is disabled
        throw new Error('Not accepting menu migrations at this time.');
      } else {
        throw new Error('Unknown error occurred while updating menu.');
      }
    }
  }
  mapWoflowLinkConfigToMigrationType(linkConfigurationType) {
    switch (linkConfigurationType) {
      case WoflowLinkConfiguration.MIGRATE_MENU:
        return MenuMigrationStartedRequest.Type.MIGRATION;
      case WoflowLinkConfiguration.UPLOAD_MENU:
        return MenuMigrationStartedRequest.Type.UPLOAD;
      default:
        throw new Error(`Unsupported WoflowLinkConfiguration: ${linkConfigurationType}`);
    }
  }
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "userInfo", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "locationSelector", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "merchantToken", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "merchantToken"), _class.prototype), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "sortedUnits", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
})), _class));
export { WoflowLinkService as default }; // This is how TypeScript knows how to look up the service.
/** @hidden */